import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Plus, Pencil, Trash2, X, Package, ShoppingCart, Tag, Upload, Loader2, Star, Heart, FileText, Film, Image as ImageLucide, Settings, Users, BarChart3, Truck, MessageSquare, Layers, ReceiptText } from "lucide-react";
import { Link } from "react-router-dom";
import { api, resolveImageUri, formatINR } from "@/lib/api";
import { toast } from "sonner";
import ConcernsAdmin from "@/pages/admin/ConcernsAdmin";
import ContentAdmin from "@/pages/admin/ContentAdmin";
import BlogsAdmin from "@/pages/admin/BlogsAdmin";
import MediaAdmin from "@/pages/admin/MediaAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import RegistrationsAdmin from "@/pages/admin/RegistrationsAdmin";
import ReportsAdmin from "@/pages/admin/ReportsAdmin";
import ReviewsAdmin from "@/pages/admin/ReviewsAdmin";
import CategoriesAdmin from "@/pages/admin/CategoriesAdmin";
import CentresAdmin from "@/pages/admin/CentresAdmin";

const EMPTY_PRODUCT = {
  name: "", category: "Supplements", description: "", price: "", mrp: "", offer_price: "", guest_discount_percent: "",
  weight: "", weight_grams: "", bv: "", stock: 100, dosage: "", ingredients: "", benefits: "",
  image: "", images: [], bestseller: false, featured: false, buy3get1: false,
};

const inputCls = "w-full h-11 rounded-lg border border-line px-3 text-sm mt-1 focus:outline-none focus:border-brand";

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs font-semibold text-muted2 uppercase tracking-wide">{label}</span>{children}</label>
);

function AdminLock({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.verifyAdminPin(pin);
      onUnlock(res.session_token);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-mist flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-paper rounded-2xl p-8 w-full max-w-sm text-center">
        <div className="w-14 h-14 rounded-full bg-brandter flex items-center justify-center mx-auto mb-3"><Lock className="text-brand" size={26} /></div>
        <h1 className="font-display text-3xl text-ink">Admin Access</h1>
        <p className="text-sm text-muted2 mb-5">Enter your admin PIN to continue.</p>
        <input value={pin} onChange={(e) => setPin(e.target.value)} type="password" inputMode="numeric" placeholder="PIN" className="w-full h-12 rounded-lg border border-line px-4 text-center tracking-widest focus:outline-none focus:border-brand" data-testid="admin-pin-input" autoFocus />
        <button disabled={loading} className="w-full h-12 mt-4 rounded-lg bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="admin-pin-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Unlock"}
        </button>
        <Link to="/" className="block text-xs text-muted2 mt-4">← Back to store</Link>
      </form>
    </div>
  );
}

function ProductForm({ initial, categories, onClose, onSaved }) {
  const [f, setF] = useState(() => {
    const base = { ...EMPTY_PRODUCT, ...initial };
    const mrp = Number(base.mrp) || 0, offer = Number(base.offer_price) || 0;
    if (!base.guest_discount_percent && mrp > 0 && offer > 0 && offer < mrp) {
      base.guest_discount_percent = Math.round(((mrp - offer) / mrp) * 100);
    }
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const doImport = async () => {
    if (!importUrl.trim()) { toast.error("Paste an altosindia.net product link"); return; }
    setImporting(true);
    try {
      const r = await api.importProductUrl(importUrl.trim());
      setF((s) => ({
        ...s,
        name: r.name || s.name,
        description: r.description || s.description,
        weight: r.weight || s.weight,
        weight_grams: r.weight_grams || s.weight_grams,
        dosage: r.dosage || s.dosage,
        mrp: r.mrp || s.mrp,
        ingredients: r.ingredients || s.ingredients,
        benefits: r.benefits || s.benefits,
        images: (r.images && r.images.length) ? r.images : s.images,
        image: (r.images && r.images[0]) ? r.images[0] : s.image,
      }));
      toast.success("Details auto-filled — review & save");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  const upload = async (e, isGallery) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      if (isGallery) setF((s) => ({ ...s, images: [...(s.images || []), res.image_url] }));
      else setF((s) => ({ ...s, image: res.image_url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const pct = Number(f.guest_discount_percent) || 0;
      const mrp = Number(f.mrp) || 0;
      const computedOffer = pct > 0 ? Math.round(mrp * (1 - pct / 100)) : 0;
      const { guest_discount_percent, ...rest } = f;
      const payload = {
        ...rest,
        price: Number(f.price) || 0,
        mrp,
        offer_price: computedOffer,
        weight_grams: Number(f.weight_grams) || 0,
        bv: Number(f.bv) || 0,
        stock: Number(f.stock) || 0,
      };
      if (initial?.id) await api.updateProduct(initial.id, payload);
      else await api.createProduct(payload);
      toast.success("Product saved");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-paper rounded-2xl w-full max-w-2xl my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-3xl text-ink">{initial?.id ? "Edit" : "Add"} Product</h2>
          <button onClick={onClose} className="p-1 text-muted2"><X size={22} /></button>
        </div>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 rounded-xl border border-brand/30 bg-mist p-3">
            <p className="text-xs font-semibold text-brand uppercase tracking-wide mb-2">Auto-fill from altosindia.net link</p>
            <div className="flex gap-2">
              <input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="Paste product URL…" className="flex-1 h-11 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand" data-testid="import-url-input" />
              <button type="button" onClick={doImport} disabled={importing} className="h-11 px-4 rounded-lg bg-brand text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60 whitespace-nowrap" data-testid="import-url-button">
                {importing ? <Loader2 className="animate-spin" size={16} /> : "Auto-fill"}
              </button>
            </div>
          </div>
          <Field label="Name"><input value={f.name} onChange={set("name")} className={inputCls} data-testid="form-name" /></Field>
          <Field label="Category">
            <select value={f.category} onChange={set("category")} className={inputCls} data-testid="form-category">
              {[...new Set([f.category, ...categories])].filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <label className="block sm:col-span-2"><span className="text-xs font-semibold text-muted2 uppercase tracking-wide">Description</span>
            <textarea value={f.description} onChange={set("description")} rows={2} className="w-full rounded-lg border border-line px-3 py-2 text-sm mt-1 focus:outline-none focus:border-brand" data-testid="form-description" /></label>
          <Field label="DP Price (Altos)"><input type="number" value={f.price} onChange={set("price")} className={inputCls} data-testid="form-price" /></Field>
          <Field label="MRP"><input type="number" value={f.mrp} onChange={set("mrp")} className={inputCls} data-testid="form-mrp" /></Field>
          <Field label="Guest Discount % (off MRP)">
            <input type="number" min="0" max="100" value={f.guest_discount_percent} onChange={set("guest_discount_percent")} placeholder="e.g. 10" className={inputCls} data-testid="form-guest-discount" />
            <span className="block text-[11px] text-muted2 mt-1" data-testid="guest-price-preview">
              {(Number(f.mrp) > 0 && Number(f.guest_discount_percent) > 0)
                ? `Guest pays ${formatINR(Math.round(Number(f.mrp) * (1 - Number(f.guest_discount_percent) / 100)))} (MRP ${formatINR(Number(f.mrp))})`
                : "Leave blank to sell to guests at MRP"}
            </span>
          </Field>
          <Field label="BV Points"><input type="number" value={f.bv} onChange={set("bv")} className={inputCls} data-testid="form-bv" /></Field>
          <Field label="Weight (text)"><input value={f.weight} onChange={set("weight")} placeholder="e.g. 60 capsules" className={inputCls} data-testid="form-weight" /></Field>
          <Field label="Weight (grams)"><input type="number" value={f.weight_grams} onChange={set("weight_grams")} className={inputCls} data-testid="form-weight-grams" /></Field>
          <Field label="Stock"><input type="number" value={f.stock} onChange={set("stock")} className={inputCls} data-testid="form-stock" /></Field>
          <Field label="Dosage"><input value={f.dosage} onChange={set("dosage")} className={inputCls} data-testid="form-dosage" /></Field>
          <label className="block sm:col-span-2"><span className="text-xs font-semibold text-muted2 uppercase tracking-wide">Ingredients</span>
            <textarea value={f.ingredients} onChange={set("ingredients")} rows={2} className="w-full rounded-lg border border-line px-3 py-2 text-sm mt-1 focus:outline-none focus:border-brand" data-testid="form-ingredients" /></label>
          <label className="block sm:col-span-2"><span className="text-xs font-semibold text-muted2 uppercase tracking-wide">Benefits</span>
            <textarea value={f.benefits} onChange={set("benefits")} rows={2} className="w-full rounded-lg border border-line px-3 py-2 text-sm mt-1 focus:outline-none focus:border-brand" data-testid="form-benefits" /></label>

          <div className="sm:col-span-2">
            <span className="text-xs font-semibold text-muted2 uppercase tracking-wide">Main image</span>
            <div className="flex items-center gap-3 mt-1">
              {f.image && <img src={resolveImageUri(f.image)} alt="" className="w-16 h-16 rounded-lg object-cover" />}
              <label className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-line text-sm font-semibold cursor-pointer">
                <Upload size={16} /> {uploading ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, false)} data-testid="form-image-upload" />
              </label>
              <input value={f.image} onChange={set("image")} placeholder="or paste image URL" className="flex-1 h-11 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand" />
            </div>
          </div>

          <div className="sm:col-span-2 flex items-center gap-6 flex-wrap">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.bestseller} onChange={set("bestseller")} data-testid="form-bestseller" /> Bestseller</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={set("featured")} data-testid="form-featured" /> Featured</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.buy3get1} onChange={set("buy3get1")} data-testid="form-buy3get1" /> Buy 3 Get 1 Free</label>
          </div>

          <div className="sm:col-span-2 flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-12 rounded-full border border-line font-semibold">Cancel</button>
            <button disabled={saving} type="submit" className="flex-1 h-12 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="form-save">
              {saving ? <Loader2 className="animate-spin" size={18} /> : "Save product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProductsTab({ categories }) {
  const qc = useQueryClient();
  const { data: products = [] } = useQuery({ queryKey: ["products", "All"], queryFn: () => api.listProducts("All") });
  const [editing, setEditing] = useState(null);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.deleteProduct(id); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["products"] }); }
    catch (err) { toast.error(err.message); }
  };
  const saved = () => { setEditing(null); qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["categories"] }); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-ink">Products ({products.length})</h2>
        <button onClick={() => setEditing({})} className="h-10 px-4 rounded-full bg-brand text-white font-semibold text-sm flex items-center gap-1.5" data-testid="add-product-button"><Plus size={16} /> Add product</button>
      </div>
      <div className="space-y-2">
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-xl border border-line p-3" data-testid={`admin-product-${p.id}`}>
            <img src={resolveImageUri(p.image)} alt="" className="w-12 h-12 rounded-lg object-cover bg-mist" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-ink text-sm truncate">{p.name} {p.bestseller && <Star size={12} className="inline fill-warn text-warn" />}</p>
              <p className="text-xs text-muted2">{p.category} · MRP {formatINR(p.mrp)} · DP {formatINR(p.price)} · Stock {p.stock}</p>
            </div>
            <button onClick={() => setEditing(p)} className="p-2 text-muted2 hover:text-brand" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>
            <button onClick={() => remove(p.id)} className="p-2 text-muted2 hover:text-err" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && <ProductForm initial={editing} categories={categories} onClose={() => setEditing(null)} onSaved={saved} />}
    </div>
  );
}

function OrdersTab() {
  const { data: orders = [], refetch } = useQuery({ queryKey: ["admin-orders"], queryFn: () => api.listOrders() });
  const [syncing, setSyncing] = useState(false);
  const setStatus = async (id, status) => {
    try { await api.updateOrderStatus(id, { status }); toast.success(`Marked ${status}`); refetch(); }
    catch (err) { toast.error(err.message); }
  };
  const bvDone = async (id) => {
    try { await api.markInstantBvDone(id); toast.success("BV marked credited"); refetch(); }
    catch (err) { toast.error(err.message); }
  };
  const sync = async () => {
    setSyncing(true);
    try { const r = await api.shiprocketSync(); toast.success(`Shiprocket sync done — ${r.updated?.length || 0} order(s) updated`); refetch(); }
    catch (err) { toast.error(err.message); } finally { setSyncing(false); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-ink">Orders ({orders.length})</h2>
        <button onClick={sync} disabled={syncing} className="h-10 px-4 rounded-full border border-line text-sm font-semibold text-brand flex items-center gap-1.5 disabled:opacity-60" data-testid="shiprocket-sync-button">
          {syncing ? <Loader2 className="animate-spin" size={15} /> : <Truck size={15} />} Sync Shiprocket
        </button>
      </div>
      <div className="space-y-3">
        {orders.length === 0 && <p className="text-muted2 text-sm">No orders yet.</p>}
        {orders.map((o) => (
          <div key={o.id} className="rounded-xl border border-line p-4" data-testid={`admin-order-${o.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono font-semibold text-ink text-sm">{o.id.slice(0, 8).toUpperCase()}</span>
              <span className="text-xs font-semibold capitalize text-brand">{o.status}</span>
            </div>
            <p className="text-sm text-ink mt-1">{o.customer?.name} · {o.customer?.phone}</p>
            <p className="text-xs text-muted2">{o.items?.map((i) => `${i.name}×${i.quantity}`).join(", ")}</p>
            {(o.awb || o.courier_name) && <p className="text-xs text-muted2 mt-1">Shipment: {o.courier_name || ""} {o.awb ? `· AWB ${o.awb}` : ""}</p>}
            <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
              <span className="font-semibold text-ink">{formatINR(o.total_billing || o.amount)}</span>
              <div className="flex gap-2">
                {["paid", "shipped", "delivered"].map((s) => (
                  <button key={s} onClick={() => setStatus(o.id, s)} className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${o.status === s ? "bg-brand text-white border-brand" : "border-line text-muted2"}`} data-testid={`set-status-${o.id}-${s}`}>{s}</button>
                ))}
              </div>
            </div>
            {o.altos_verified && (
              <div className="mt-2 flex items-center justify-between rounded-lg bg-mist px-3 py-1.5">
                <span className="text-xs text-ink flex items-center gap-1"><Star size={12} className="fill-brand text-brand" /> BV {o.total_bv || 0}{o.instant_bv_requested ? " · instant requested" : ""}</span>
                {o.instant_bv_processed ? (
                  <span className="text-xs font-semibold text-ok">BV credited ✓</span>
                ) : (o.instant_bv_requested || o.altos_verified) ? (
                  <button onClick={() => bvDone(o.id)} data-testid={`bv-done-${o.id}`} className="text-xs font-semibold text-brand">Mark BV credited</button>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CouponsTab() {
  const { data: coupons = [], refetch } = useQuery({ queryKey: ["coupons"], queryFn: () => api.listCoupons() });
  const [show, setShow] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ code: "", description: "", discount_type: "percent", value: "", audience: "non_altos", min_order: "", start_date: today, end_date: today });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.createCoupon({ ...f, value: Number(f.value) || 0, min_order: Number(f.min_order) || 0 });
      toast.success("Coupon created"); setShow(false); refetch();
      setF({ code: "", description: "", discount_type: "percent", value: "", audience: "non_altos", min_order: "", start_date: today, end_date: today });
    } catch (err) { toast.error(err.message); }
  };
  const remove = async (id) => { try { await api.deleteCoupon(id); toast.success("Deleted"); refetch(); } catch (err) { toast.error(err.message); } };
  const inputCls = "w-full h-11 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-ink">Coupons ({coupons.length})</h2>
        <button onClick={() => setShow((s) => !s)} className="h-10 px-4 rounded-full bg-brand text-white font-semibold text-sm flex items-center gap-1.5" data-testid="add-coupon-button"><Plus size={16} /> New coupon</button>
      </div>
      {show && (
        <form onSubmit={create} className="rounded-xl border border-line p-4 mb-4 grid sm:grid-cols-2 gap-3">
          <input value={f.code} onChange={set("code")} placeholder="CODE" className={inputCls} data-testid="coupon-code" />
          <input value={f.description} onChange={set("description")} placeholder="Description" className={inputCls} />
          <select value={f.discount_type} onChange={set("discount_type")} className={inputCls}><option value="percent">Percent %</option><option value="flat">Flat ₹</option></select>
          <input type="number" value={f.value} onChange={set("value")} placeholder="Value" className={inputCls} data-testid="coupon-value" />
          <select value={f.audience} onChange={set("audience")} className={inputCls}><option value="non_altos">Non-Altos</option><option value="altos">Altos ID</option></select>
          <input type="number" value={f.min_order} onChange={set("min_order")} placeholder="Min order ₹" className={inputCls} />
          <label className="text-xs text-muted2">Start<input type="date" value={f.start_date} onChange={set("start_date")} className={inputCls} /></label>
          <label className="text-xs text-muted2">End<input type="date" value={f.end_date} onChange={set("end_date")} className={inputCls} /></label>
          <button className="sm:col-span-2 h-11 rounded-full bg-brand text-white font-semibold" data-testid="coupon-create-submit">Create coupon</button>
        </form>
      )}
      <div className="space-y-2">
        {coupons.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-xl border border-line p-3" data-testid={`admin-coupon-${c.code}`}>
            <div>
              <p className="font-semibold text-ink text-sm">{c.code} <span className="text-xs text-muted2 font-normal">· {c.discount_type === "percent" ? `${c.value}%` : formatINR(c.value)} · {c.audience === "altos" ? "Altos" : "Guest"} · used {c.used_count || 0}</span></p>
              <p className="text-xs text-muted2">{c.description}</p>
            </div>
            <button onClick={() => remove(c.id)} className="p-2 text-muted2 hover:text-err"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
  const [tab, setTab] = useState("products");
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.categories(), enabled: !!token });

  useEffect(() => {
    if (token) sessionStorage.setItem("admin_token", token);
  }, [token]);

  if (!token) return <AdminLock onUnlock={setToken} />;

  const tabs = [
    { id: "products", label: "Products", icon: Package },
    { id: "categories", label: "Categories", icon: Layers },
    { id: "orders", label: "Orders", icon: ShoppingCart },
    { id: "coupons", label: "Coupons", icon: Tag },
    { id: "reviews", label: "Reviews", icon: MessageSquare },
    { id: "concerns", label: "Concerns", icon: Heart },
    { id: "blogs", label: "Blogs", icon: Film },
    { id: "content", label: "Content", icon: FileText },
    { id: "media", label: "Media", icon: ImageLucide },
    { id: "registrations", label: "Sellers", icon: Users },
    { id: "centres", label: "Centres", icon: ReceiptText },
    { id: "reports", label: "Reports", icon: BarChart3 },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-mist">
      <header className="bg-paper border-b border-line sticky top-0 z-30">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/altos-logo.jpg" alt="" className="h-10 w-auto" />
            <span className="font-display text-2xl text-ink">Admin</span>
          </div>
          <button onClick={() => { setToken(""); sessionStorage.removeItem("admin_token"); }} className="text-sm font-semibold text-err">Lock</button>
        </div>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 flex gap-1 overflow-x-auto hide-scrollbar">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`shrink-0 flex items-center gap-1.5 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? "border-brand text-brand" : "border-transparent text-muted2"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        {tab === "products" && <ProductsTab categories={categories} />}
        {tab === "categories" && <CategoriesAdmin />}
        {tab === "orders" && <OrdersTab />}
        {tab === "coupons" && <CouponsTab />}
        {tab === "reviews" && <ReviewsAdmin />}
        {tab === "concerns" && <ConcernsAdmin />}
        {tab === "blogs" && <BlogsAdmin />}
        {tab === "content" && <ContentAdmin />}
        {tab === "media" && <MediaAdmin />}
        {tab === "registrations" && <RegistrationsAdmin />}
        {tab === "centres" && <CentresAdmin />}
        {tab === "reports" && <ReportsAdmin />}
        {tab === "settings" && <SettingsAdmin />}
      </main>
    </div>
  );
}
